import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Form, Input, Button, Alert, Typography, Space, Divider, Tooltip, Modal, message } from 'antd';
import { UserOutlined, LockOutlined, MedicineBoxOutlined, PhoneOutlined, MailOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import api from '../api';
import { usePatientPortal } from '../context/PatientPortalContext';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../components/LanguageToggle';

const { Title, Text, Paragraph } = Typography;

const PatientPortalLoginPage = () => {
  const navigate = useNavigate();
  const { loginWithCredentials, isAuthenticated, loading } = usePatientPortal();
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [forgotPasswordForm] = Form.useForm();
  const [loginLoading, setLoginLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [forgotPasswordVisible, setForgotPasswordVisible] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/portal/dashboard');
    }
  }, [isAuthenticated, navigate]);



  const handleLogin = async (values) => {
    setLoginLoading(true);
    setError('');

    try {
      // Credentials-based login only
      if (!values.username || !values.password) {
        setError('Please provide email/phone and password');
        return;
      }

      const result = await loginWithCredentials(values.username, values.password);

      if (result.success) {
        // Login successful, navigation will be handled by useEffect
        console.log('Portal login successful');
      } else {
        setError(result.message || t('auth.loginFailed'));
        setLoginAttempts(prev => prev + 1);

        // Clear password field on failed attempt
        form.setFieldsValue({ password: '' });
      }
    } catch (err) {
      setError(t('messages.serverError'));
      setLoginAttempts(prev => prev + 1);
    } finally {
      setLoginLoading(false);
    }
  };

  const isAccountLocked = loginAttempts >= 5;

  const handleForgotPassword = async (values) => {
    setForgotPasswordLoading(true);

    try {
      const response = await api.post('/portal/forgot-password', {
        emailOrPhone: values.emailOrPhone
      });

      if (response.data.status === 'success') {
        setForgotPasswordSuccess(true);
        forgotPasswordForm.resetFields();
        message.success('If an account exists, a new password has been sent to your email.');
      } else {
        message.error('Unable to process your request. Please try again.');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      message.error('Unable to process your request. Please try again or contact support.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleForgotPasswordCancel = () => {
    setForgotPasswordVisible(false);
    setForgotPasswordSuccess(false);
    forgotPasswordForm.resetFields();
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Card style={{ width: 400, textAlign: 'center' }}>
          <Space direction="vertical" size="large">
            <MedicineBoxOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            <Text>{t('common.loading')}</Text>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      position: 'relative'
    }}>
      <div style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 1000 }}>
        <LanguageToggle type="default" />
      </div>
      <Card
        style={{
          width: '100%',
          maxWidth: 450,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          borderRadius: '12px'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <MedicineBoxOutlined
            style={{
              fontSize: '48px',
              color: '#1890ff',
              marginBottom: '16px'
            }}
          />
          <Title level={2} style={{ margin: 0, color: '#262626' }}>
            {t('auth.portalLoginTitle')}
          </Title>
          <Text type="secondary">
            {t('auth.portalSubtitle')}
          </Text>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
            closable
            onClose={() => setError('')}
          />
        )}

        {isAccountLocked && (
          <Alert
            message={t('auth.accountLocked')}
            description="Too many failed login attempts. Please wait a few minutes before trying again or contact your healthcare provider for assistance."
            type="warning"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}



        <Form
          form={form}
          name="portal-login"
          onFinish={handleLogin}
          layout="vertical"
          size="large"
          disabled={isAccountLocked}
        >
          <Form.Item
            name="username"
            label="Email or Phone Number"
            rules={[
              { required: true, message: 'Please enter your email address or phone number' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Enter your email or phone number"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter your password' },
              { min: 8, message: t('auth.passwordMinLength') }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter your password"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: '16px' }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loginLoading}
              disabled={isAccountLocked}
              style={{
                width: '100%',
                height: '48px',
                fontSize: '16px',
                borderRadius: '8px'
              }}
            >
              {loginLoading ? t('auth.signingIn') : t('auth.signInToPortal')}
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <Button
              type="link"
              onClick={() => setForgotPasswordVisible(true)}
              style={{ padding: 0, fontSize: '14px' }}
              icon={<QuestionCircleOutlined />}
            >
              Forgot your password?
            </Button>
          </div>
        </Form>

        <Divider />

        <div style={{ textAlign: 'center' }}>
          <Paragraph type="secondary" style={{ fontSize: '14px', margin: 0 }}>
            <strong>Need help?</strong>
          </Paragraph>
          <Paragraph type="secondary" style={{ fontSize: '13px', marginTop: '8px' }}>
            If you're having trouble accessing your portal, please contact your healthcare provider. 
            Keep your access credentials secure and do not share them with others.
          </Paragraph>
        </div>

        {loginAttempts > 0 && loginAttempts < 5 && (
          <Alert
            message={`Login attempt ${loginAttempts}/5`}
            description="Your account will be temporarily locked after 5 failed attempts."
            type="info"
            showIcon
            style={{ marginTop: '16px' }}
          />
        )}
      </Card>

      {/* Forgot Password Modal */}
      <Modal
        title="Reset Your Password"
        open={forgotPasswordVisible}
        onCancel={handleForgotPasswordCancel}
        footer={null}
        width={450}
        maskClosable={false}
      >
        {!forgotPasswordSuccess ? (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Enter your email address or phone number associated with your patient portal account.
                If an account exists, we'll send a new password to your email.
              </Text>
            </div>

            <Form
              form={forgotPasswordForm}
              name="forgot-password"
              onFinish={handleForgotPassword}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="emailOrPhone"
                label="Email Address or Phone Number"
                rules={[
                  { required: true, message: 'Please enter your email address or phone number' }
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Enter your email or phone number"
                  style={{ borderRadius: '6px' }}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: '16px' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={forgotPasswordLoading}
                  style={{
                    width: '100%',
                    height: '44px',
                    borderRadius: '6px'
                  }}
                >
                  {forgotPasswordLoading ? 'Sending...' : 'Reset Password'}
                </Button>
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  onClick={handleForgotPasswordCancel}
                  style={{
                    width: '100%',
                    height: '40px',
                    borderRadius: '6px'
                  }}
                >
                  Cancel
                </Button>
              </Form.Item>
            </Form>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ marginBottom: '16px' }}>
              <MailOutlined style={{ fontSize: '48px', color: '#52c41a' }} />
            </div>
            <Title level={4} style={{ color: '#52c41a', margin: '0 0 16px 0' }}>
              Password Reset Sent!
            </Title>
            <Paragraph style={{ fontSize: '14px', marginBottom: '24px' }}>
              If an account with that email or phone exists, a new password has been sent to your email address.
              Please check your inbox and use the new password to log in.
            </Paragraph>
            <Button
              type="primary"
              onClick={handleForgotPasswordCancel}
              style={{
                height: '40px',
                borderRadius: '6px',
                paddingLeft: '24px',
                paddingRight: '24px'
              }}
            >
              Return to Login
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PatientPortalLoginPage;
